/**
 * MultipleLinesInput – dynamic list of text inputs with add/remove.
 * Used for repeatable text fields (e.g., specific objectives, key deliverables, checklists).
 * Similar to UFS but with a more descriptive name for multi-line list scenarios.
 */
import React from 'react';
import { Plus } from 'lucide-react';

const MULTIPLE_LINES_INPUT_CLASS =
  'w-full rounded-lg px-2 py-1.5 text-xs text-slate-700 border border-gray-300 bg-white shadow-sm focus:border-primary-dark focus:outline-none focus:ring-1 focus:ring-primary-dark/40 transition';

/**
 * @param {Object} props
 * @param {string} [props.label] - Field label
 * @param {boolean} [props.required] - Whether the field is required
 * @param {string[]} props.value - Array of text values
 * @param {function(string[]): void} props.onChange - Called with updated array
 * @param {string} [props.placeholder] - Input placeholder
 * @param {boolean} [props.disabled] - Disable inputs
 * @param {string} [props.error] - Error message
 * @param {string} [props.inputClassName] - Custom class for inputs (defaults to MULTIPLE_LINES_INPUT_CLASS)
 * @param {'left'|'top'} [props.labelPosition='top'] - 'top' = label above first row; 'left' = label beside first row
 * @param {number} [props.labelWidthRem=8] - Width of label column when labelPosition is 'left'
 */
export const MultipleLinesInput = ({
  label,
  required = false,
  value,
  onChange,
  placeholder,
  disabled = false,
  error,
  inputClassName = MULTIPLE_LINES_INPUT_CLASS,
  labelWidthRem = 8,
  labelPosition = 'top',
}) => {
  const items = Array.isArray(value) ? value : value ? [value] : [''];
  const isTop = labelPosition === 'top';
  const gridStyle = isTop ? undefined : { gridTemplateColumns: `${labelWidthRem}rem 1fr` };

  const handleItemChange = (idx, v) => {
    const next = [...items];
    next[idx] = v;
    onChange(next);
  };

  const handleAdd = () => onChange([...items, '']);

  const handleRemove = (idx) => {
    if (items.length <= 1) return;
    const next = items.filter((_, i) => i !== idx);
    onChange(next);
  };

  const labelEl = label ? (
    <label className="text-sm font-medium text-primary-dark">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
  ) : null;

  const inputRow = (item, idx) => (
    <div key={idx} className="min-w-0 flex gap-2 items-center">
      <input
        type="text"
        value={item}
        onChange={(e) => handleItemChange(idx, e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={inputClassName}
      />
      {idx === items.length - 1 ? (
        <button
          type="button"
          onClick={handleAdd}
          disabled={disabled}
          className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg text-primary-dark border border-gray-300 bg-white hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          aria-label="Add"
        >
          <Plus className="h-4 w-4" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => handleRemove(idx)}
          disabled={disabled}
          className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg text-red-600 border border-gray-300 bg-white hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
          aria-label="Remove"
        >
          ×
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-2">
      {isTop && labelEl}
      {items.map((item, idx) =>
        isTop ? (
          <div key={idx}>{inputRow(item, idx)}</div>
        ) : (
          <div key={idx} className="grid items-center" style={gridStyle}>
            {idx === 0 ? labelEl ?? <div /> : <div />}
            {inputRow(item, idx)}
          </div>
        )
      )}
      {error && (
        <div className={isTop ? '' : 'grid'} style={isTop ? undefined : gridStyle}>
          {!isTop && <div />}
          <p className="mt-1 text-xs text-red-600">{error}</p>
        </div>
      )}
    </div>
  );
};

export default MultipleLinesInput;
